import * as React from 'react'
import { tv } from 'tailwind-variants'
import CircularProgress from '../atoms/CircularProgress'

export interface UploadProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  file?: File
  status: 'idle' | 'uploading' | 'success' | 'error'
  progress?: number
  error?: string | string[]
  onCancel?: () => void
  size?: 'sm' | 'default' | 'lg'
  documentType?: 'receita' | 'identidade' | 'comprovante' | 'vinculo' | 'identidade_responsavel'
  onKeepDocument?: () => void
  documentId?: string
}

const uploadProgressVariants = tv({
  base: 'w-full rounded-sm border border-gray-300 bg-gray-50 transition-all duration-200',
  variants: {
    status: {
      idle: 'border-gray-200',
      uploading: 'border-gray-200',
      success: 'border-gray-200 bg-primary-200 bg-opacity-30',
      error: 'border-gray-200'
    },
    size: {
      sm: 'space-y-2 p-3',
      default: 'space-y-3 p-4',
      lg: 'space-y-4 p-6'
    }
  },
  defaultVariants: {
    status: 'idle',
    size: 'default'
  }
})

const progressBarVariants = tv({
  base: 'w-full overflow-hidden rounded-full transition-all duration-300',
  variants: {
    size: {
      sm: 'h-1.5',
      default: 'h-2',
      lg: 'h-3'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return ''
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toUpperCase() || 'FILE'
}

const getDocumentTypeName = (documentType?: string): string => {
  switch (documentType) {
    case 'prescription':
      return 'Receita'
    case 'identity':
      return 'Documento de identidade'
    case 'address':
      return 'Comprovante de endereço'
    case 'affiliation':
      return 'Documento de vínculo ou identidade do responsável'
    case 'anvisa':
      return 'Anvisa'
    case 'responsibleIdentity':
      return 'Identidade do responsável'
    default:
      return 'documento válido'
  }
}

const DeleteIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 12V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 12V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M6 10V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ExtensionIcon = ({ extension, size = 'default' }: { extension: string; size?: 'sm' | 'default' | 'lg' }) => {
  const iconSize = size === 'sm' ? 'w-8 h-10' : size === 'lg' ? 'w-12 h-14' : 'w-10 h-12'
  const textSize = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-xs'

  const getExtensionColor = (ext: string) => {
    switch (ext.toLowerCase()) {
      case 'pdf':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'doc':
      case 'docx':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'xls':
      case 'xlsx':
        return 'bg-green-100 text-green-700 border-green-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  return (
    <div
      className={`${iconSize} ${getExtensionColor(extension)} flex flex-col items-center justify-center rounded-lg border shadow-sm`}>
      <svg
        className={`${size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'} mb-0.5`}
        fill="currentColor"
        viewBox="0 0 24 24">
        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" />
        <path d="M14 2V8H20" />
      </svg>
      <span className={`${textSize} font-bold leading-none`}>{extension}</span>
    </div>
  )
}

const UploadProgress = React.forwardRef<HTMLDivElement, UploadProgressProps>(
  (
    {
      className,
      file,
      status = 'idle',
      progress = 0,
      error,
      onCancel,
      size = 'default',
      documentType,
      onKeepDocument,
      documentId,
      ...props
    },
    ref
  ) => {
    if (!file && status === 'idle') {
      return null
    }
    console.log('file', file)
    const renderCircularProgress = () => {
      return <CircularProgress progress={progress} status={status} />
    }

    const renderExtensionIcon = () => {
      if (!file) return null

      return <ExtensionIcon extension={getFileExtension(file.name)} size={size} />
    }

    const renderStatusText = () => {
      switch (status) {
        case 'uploading':
          return `Enviando documento... ${progress}%`
        case 'success':
          return 'Documento enviado com sucesso!'
        case 'error':
          return 'Erro no envio do documento'
        default:
          return 'Documento selecionado'
      }
    }

    console.log('documentId dentro de UploadProgress', documentId)

    const renderErrors = () => {
      if (!error || status !== 'error') return null

      const errors = Array.isArray(error) ? error : [error]
      const textSize = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-sm'

      const processedErrors: string[] = []

      errors.forEach((err) => {
        if (typeof err === 'string' && err.includes('Request failed with status code 500')) {
          const documentTypeName = getDocumentTypeName(documentType)
          processedErrors.push(`Esse documento não é um **${documentTypeName}**`)
        } else if (typeof err === 'string' && err.includes('• **')) {
          const validationErrors = err.split('• **').filter((part) => part.trim())

          validationErrors.forEach((validation, index) => {
            if (index === 0) {
              const parts = validation.split('**')
              if (parts.length >= 2) {
                processedErrors.push(`**${parts[1]}**${parts.slice(2).join('**')}`)
              } else {
                processedErrors.push(validation)
              }
            } else {
              processedErrors.push(`**${validation}`)
            }
          })
        } else {
          processedErrors.push(err)
        }
      })

      return (
        <div className={`${textSize} space-y-2 text-red-600`}>
          {processedErrors.map((err, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="mt-0.5 flex-shrink-0 text-red-500">•</span>
              <span
                className="break-words"
                dangerouslySetInnerHTML={{
                  __html: err.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }}
              />
            </div>
          ))}

          {onKeepDocument && (
            <div className="flex flex-col gap-3 text-xs text-gray-700 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
              <span
                className={`text-center font-medium sm:text-left ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-sm'}`}>
                Deseja manter esse documento?
              </span>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <button
                  className="w-full rounded-md border border-none border-primary-600 bg-transparent p-2 px-4 text-center text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600 sm:w-auto"
                  onClick={onKeepDocument}>
                  <span className="text-sm font-semibold">Manter documento</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )
    }

    return (
      <div ref={ref} className={uploadProgressVariants({ status, size, className })} {...props}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            {renderExtensionIcon()}

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`truncate font-medium text-gray-900 ${
                    size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-base' : 'text-sm'
                  }`}>
                  {file?.name || 'Arquivo selecionado'}
                </span>

                {file && (
                  <>
                    <span className={`text-gray-500 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>•</span>
                    <span className={`font-medium text-gray-600 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
                      {formatFileSize(file.size)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {renderCircularProgress()}

            {onCancel && (
              <button
                onClick={onCancel}
                className={`flex-shrink-0 border border-none bg-transparent text-gray-400 transition-colors hover:text-gray-600 ${
                  size === 'sm' ? 'p-1' : 'p-1.5'
                }`}
                type="button">
                <DeleteIcon className={size === 'sm' ? 'h-4 w-4' : 'h-6 w-6'} />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className={progressBarVariants({ size })}>
            <div className="h-full rounded-full bg-gray-200">
              <div
                className={`h-full rounded-full transition-all duration-300 ease-out ${
                  status === 'success' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          </div>
        </div>

        <div className={`text-gray-700 ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-sm'}`}>
          {renderStatusText()}
        </div>

        {renderErrors()}
      </div>
    )
  }
)

UploadProgress.displayName = 'UploadProgress'

export { UploadProgress, uploadProgressVariants }
